import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import keycloak from '../../../../Keycloak';
import DevicesOutlined from '@mui/icons-material/DevicesOutlined';
import HelpOutlined from '@mui/icons-material/HelpOutlined';
import LaunchOutlined from '@mui/icons-material/LaunchOutlined';
import LockOpenOutlined from '@mui/icons-material/LockOpenOutlined';
import LogoutOutlined from '@mui/icons-material/LogoutOutlined';
import PersonOutlined from '@mui/icons-material/PersonOutlined';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import StickyNote2Outlined from '@mui/icons-material/StickyNote2Outlined';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Avatar,
    Box,
    Chip,
    ClickAwayListener,
    Divider,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Popper,
    Stack,
    Tooltip,
    Typography
} from '@mui/material';

// third-party
import { FormattedMessage } from 'react-intl';

// project imports
import MainCard from 'components/cards/MainCard';
import Transitions from 'components/Transitions';
import { useKeycloak } from 'keycloak-react-web';

// assets

import useConfig from 'hooks/useConfig';
import { ProfileContext } from 'contexts/ProfileContext';

type ProfileSectionRoutes = '' | '/profile' | '/config/communications' | '/config/devices' | '/config/brand-settings' | '/admin';

const ProfileSection = () => {
    const theme = useTheme();
    const intl = useIntl();
    const { borderRadius } = useConfig();
    const navigate = useNavigate();
    const location = useLocation();

    const {
        keycloak: { logout }
    } = useKeycloak();
    const [open, setOpen] = useState(false);
    const { profile } = useContext(ProfileContext);

    const anchorRef = useRef<any>(null);

    const handleListItemClick = (event: React.MouseEvent<HTMLDivElement>, route: ProfileSectionRoutes) => {
        handleClose(event);

        if (route !== '') {
            navigate(route);
        }
    };
    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };
    const handleClose = (event: React.MouseEvent<HTMLDivElement> | MouseEvent | TouchEvent) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }

        setOpen(false);
    };
    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }

        prevOpen.current = open;
    }, [open]);

    return (
        <>
            <Tooltip title={intl.formatMessage({ id: 'CONFIGURACION' })} placement="bottom">
                <Chip
                    aria-label={intl.formatMessage({ id: 'CONFIGURACION' })}
                    aria-expanded={open}
                    sx={{
                        height: '48px',
                        alignItems: 'center',
                        borderRadius: '27px',
                        transition: 'all .2s ease-in-out',
                        borderColor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.grey[100],
                        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.grey[100],
                        '&[aria-controls="menu-list-grow"], &:hover': {
                            borderColor: theme.palette.grey[200],
                            background: `${theme.palette.grey[200]}!important`
                        },
                        '& .MuiChip-label': {
                            lineHeight: 0
                        },

                        '&:hover, &:active, &:focus': {
                            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.grey[400]
                        }
                    }}
                    icon={
                        <Avatar
                            src={profile?.profileImage?.base64 ? `data:image/png;base64, ${profile.profileImage.base64}` : undefined}
                            alt=""
                            sx={{
                                ...theme.typography.mediumAvatar,
                                margin: '8px 0 8px 8px !important',
                                cursor: 'pointer',
                                width: '30px',
                                height: '30px',
                                bgcolor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.background.paper
                            }}
                            ref={anchorRef}
                            aria-hidden="true"
                            color={theme.palette.grey[900]}
                        />
                    }
                    label={<SettingsOutlined aria-hidden="true" htmlColor={theme.palette.grey[900]} sx={{ fontSize: '1.5rem' }} />}
                    variant="outlined"
                    ref={anchorRef}
                    aria-controls={open ? 'menu-list-grow' : undefined}
                    aria-haspopup="true"
                    onClick={handleToggle}
                />
            </Tooltip>

            <Popper
                placement="bottom"
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                modifiers={[
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 14]
                        }
                    }
                ]}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={handleClose}>
                        <Transitions in={open} {...TransitionProps}>
                            <Paper
                                sx={{
                                    maxHeight: 500,
                                    maxWidth: 450,
                                    minWidth: 300,
                                    [theme.breakpoints.down('sm')]: {
                                        maxHeight: '80vh',
                                        minWidth: '100vw',
                                        maxWidth: '100vw',
                                        overflowY: 'auto'
                                    }
                                }}
                            >
                                {open && (
                                    <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                                        <Box sx={{ p: 2, pb: 0 }}>
                                            <Stack>
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                    <Typography variant="h4" component={'p'}>
                                                        {intl.formatMessage({ id: 'BIENVENIDO' })}
                                                    </Typography>

                                                    <Typography component="span" variant="h4" sx={{ fontWeight: 400 }}>
                                                        {profile?.name ? profile?.name.split(' ')[0] : ''}
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                        </Box>
                                        <Box sx={{ p: 2, pt: 0 }}>
                                            <Divider sx={{ mt: 2 }} />
                                            <List
                                                component="nav"
                                                sx={{
                                                    width: '100%',
                                                    maxWidth: 350,
                                                    minWidth: 300,
                                                    backgroundColor: theme.palette.background.paper,
                                                    borderRadius: '10px',
                                                    [theme.breakpoints.down('md')]: {
                                                        minWidth: '100%'
                                                    },
                                                    '& .MuiListItemButton-root': {
                                                        mt: 0.5
                                                    }
                                                }}
                                            >
                                                {!keycloak.realmAccess?.roles.includes('readonly') && (
                                                    <ListItemButton
                                                        sx={{ borderRadius: `${borderRadius}px` }}
                                                        selected={location.pathname === '/profile'}
                                                        onClick={(event: React.MouseEvent<HTMLDivElement>) =>
                                                            handleListItemClick(event, '/profile')
                                                        }
                                                    >
                                                        <ListItemIcon>
                                                            <PersonOutlined sx={{ fontSize: '1.3rem' }} />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={
                                                                <Typography variant="body2">
                                                                    <FormattedMessage id="MI_CUENTA" />
                                                                </Typography>
                                                            }
                                                        />
                                                    </ListItemButton>
                                                )}
                                                {keycloak.realmAccess?.roles.includes('company') &&
                                                    !keycloak.realmAccess?.roles.includes('readonly') && (
                                                        <ListItemButton
                                                            sx={{ borderRadius: `${borderRadius}px` }}
                                                            selected={location.pathname === '/admin'}
                                                            onClick={(event: React.MouseEvent<HTMLDivElement>) =>
                                                                handleListItemClick(event, '/admin')
                                                            }
                                                        >
                                                            <ListItemIcon>
                                                                <LockOpenOutlined sx={{ fontSize: '1.3rem' }} />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={
                                                                    <Typography variant="body2">
                                                                        <FormattedMessage id="ADMINISTRACION" />
                                                                    </Typography>
                                                                }
                                                            />
                                                        </ListItemButton>
                                                    )}
                                                {!keycloak.realmAccess?.roles.includes('readonly') && (
                                                    <ListItemButton
                                                        sx={{ borderRadius: `${borderRadius}px` }}
                                                        selected={location.pathname === '/config/brand-settings'}
                                                        onClick={(event: React.MouseEvent<HTMLDivElement>) =>
                                                            handleListItemClick(event, '/config/brand-settings')
                                                        }
                                                    >
                                                        <ListItemIcon>
                                                            <StickyNote2Outlined sx={{ fontSize: '1.3rem' }} />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={
                                                                <Typography variant="body2">
                                                                    <FormattedMessage id="BRANDING" />
                                                                </Typography>
                                                            }
                                                        />
                                                    </ListItemButton>
                                                )}

                                                {!keycloak.realmAccess?.roles.includes('readonly') && (
                                                    <ListItemButton
                                                        sx={{ borderRadius: `${borderRadius}px` }}
                                                        selected={location.pathname === '/config/communications'}
                                                        onClick={(event: React.MouseEvent<HTMLDivElement>) =>
                                                            handleListItemClick(event, '/config/communications')
                                                        }
                                                    >
                                                        <ListItemIcon>
                                                            <SettingsOutlined sx={{ fontSize: '1.3rem' }} />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={
                                                                <Typography variant="body2">
                                                                    <FormattedMessage id="CONFIGURACION_DE_COMUNICACIONES" />
                                                                </Typography>
                                                            }
                                                        />
                                                    </ListItemButton>
                                                )}
                                                {keycloak.realmAccess?.roles.includes('company') &&
                                                    !keycloak.realmAccess?.roles.includes('readonly') && (
                                                        <ListItemButton
                                                            sx={{ borderRadius: `${borderRadius}px` }}
                                                            selected={location.pathname === '/config/devices'}
                                                            onClick={(event: React.MouseEvent<HTMLDivElement>) =>
                                                                handleListItemClick(event, '/config/devices')
                                                            }
                                                        >
                                                            <ListItemIcon>
                                                                <DevicesOutlined sx={{ fontSize: '1.3rem' }} />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={
                                                                    <Typography variant="body2">
                                                                        <FormattedMessage id="DISPOSITIVOS" />
                                                                    </Typography>
                                                                }
                                                            />
                                                        </ListItemButton>
                                                    )}
                                                <ListItemButton
                                                    component="a"
                                                    href="https://rubricae.com/centro-de-ayuda/"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    sx={{ borderRadius: `${borderRadius}px`, textDecoration: 'none', color: 'inherit' }}
                                                >
                                                    <ListItemIcon>
                                                        <HelpOutlined sx={{ fontSize: '1.3rem' }} />
                                                    </ListItemIcon>

                                                    <ListItemText
                                                        primary={
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <Typography variant="body2" component="span">
                                                                    <FormattedMessage id="AYUDA" />
                                                                </Typography>
                                                                <Box sx={{ ml: '8px' }}>
                                                                    <LaunchOutlined sx={{ fontSize: '0.8rem' }} />
                                                                </Box>
                                                            </Box>
                                                        }
                                                    />
                                                </ListItemButton>
                                                <ListItemButton
                                                    sx={{ borderRadius: `${borderRadius}px` }}
                                                    selected={false}
                                                    onClick={() => logout()}
                                                >
                                                    <ListItemIcon>
                                                        <LogoutOutlined sx={{ fontSize: '1.3rem' }} />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={
                                                            <Typography variant="body2">
                                                                <FormattedMessage id="CERRAR_SESION" />
                                                            </Typography>
                                                        }
                                                    />
                                                </ListItemButton>
                                            </List>
                                        </Box>
                                    </MainCard>
                                )}
                            </Paper>
                        </Transitions>
                    </ClickAwayListener>
                )}
            </Popper>
        </>
    );
};

export default ProfileSection;
